/**
 * Venue data for the /venues surface.
 *
 * The venue set below is the real launch set. The **rates are still
 * mock** — there is no public endpoint for this yet, so `venueApy` is
 * illustrative until a retail read of the Surface catalog exists in
 * yaas-api.
 *
 * The three descriptive fields map 1:1 onto the YaaS domain model, so
 * swapping mock → live is a data-source change, not a rewrite:
 *
 *   provider    → SurfaceType.providerName        ("Raydium")
 *   productType → SurfaceCategory.name            ("Liquidity Pool")
 *   surface     → Surface.name                    ("CLMM: USDv-USDC")
 *
 * Every venue listed is **covered** — an Active Program has an Active
 * ProgramSurface pointing at it, so Solomon rewards keep accruing while
 * USDv sits there. That is the page's whole premise, so coverage is a
 * query filter rather than a per-row field: the live read should return
 * covered surfaces only.
 */

export interface Venue {
    slug: string;
    /** SurfaceType.providerName — the company. Drives the row icon. */
    provider: string;
    /** SurfaceCategory.name — what kind of product this is. */
    productType: string;
    /** Surface.name — the specific pool, including its AMM type. */
    surface: string;
    /** The venue's own rate, on top of Solomon's base. MOCK. Variable in
     *  reality, set by the protocol rather than by Solomon. */
    venueApy: number;
}

/**
 * Launch set — order as specified, deliberately NOT sorted by rate.
 * These venues carry loss modes the base rate does not, and a
 * yield-sorted list reads as a recommendation.
 */
export const MOCK_VENUES: Venue[] = [
    {
        slug: "meteora-dlmm-usdv-usdc",
        provider: "Meteora",
        productType: "Liquidity Pool",
        surface: "DLMM: USDv-USDC",
        venueApy: 7.4
    },
    {
        slug: "raydium-cpmm-usdv-usdc",
        provider: "Raydium",
        productType: "Liquidity Pool",
        surface: "CPMM: USDv-USDC",
        venueApy: 4.1
    },
    {
        slug: "raydium-clmm-usdv-usdc",
        provider: "Raydium",
        productType: "Liquidity Pool",
        surface: "CLMM: USDv-USDC",
        venueApy: 6.8
    },
    {
        slug: "orca-whirlpools-usdv-usdc",
        provider: "Orca",
        productType: "Liquidity Pool",
        surface: "Whirlpools: USDv-USDC",
        venueApy: 6.2
    }
];

/**
 * The best venue rate currently on offer. Drives the enticement figure
 * on surfaces that point at /venues (the positions ghost row), so the
 * number advertised there can never drift from the list it links to.
 */
export function leadingVenueApy(): number {
    return Math.max(...MOCK_VENUES.map((venue) => venue.venueApy));
}
